import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { animalApi, farmApi, stableApi, boxApi } from '../services/api';
import { Animal, Farm, Stable, Box } from '../types';
import { handleApiError } from '../utils/handleApiError';
import Pagination from './Pagination';
import ClearableInput from './ClearableInput';
import ClearableSelect from './ClearableSelect';

export interface AnimalWarning {
  type: 'already_shipped' | 'in_other_draft' | 'not_slaughterable' | 'not_active';
  delivery_id?: number;
  delivery_date?: string;
  buyer?: string;
  withdrawal_end_date?: string;
  current_status?: string;
}

export interface SelectedAnimal {
  id: number;
  ear_tag: string;
  breed?: string;
  delivery_warnings?: AnimalWarning[];
}

interface AnimalSelectorModalProps {
  /** Controls modal visibility */
  isOpen: boolean;
  /** Called when the modal is dismissed (cancel/close) */
  onClose: () => void;
  /** Called when animals are selected. Single mode: array of one. Multi mode: array of all selected. */
  onSelect: (animals: SelectedAnimal[]) => void;
  /** 'single' = click row to select, 'multi' = checkboxes + confirm button */
  mode: 'single' | 'multi';
  /** Animal IDs to hide from the list (e.g., already-assigned animals) */
  excludeIds?: number[];
  /** Default server-side status filter (e.g., 'aktiv'). User can change it via dropdown. */
  filterByStatus?: string;
  /** Whether to show the status filter dropdown (allows user to change from default) */
  showStatusFilter?: boolean;
  /** Whether to load and show delivery conflict warnings per animal */
  includeDeliveryWarnings?: boolean;
  /** Modal header text */
  title?: string;
  /** Multi-mode confirm button label */
  confirmLabel?: string;
}

const formatWarningDate = (dateStr?: string) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('hu-HU');
};

export const warningBadge = (warning: AnimalWarning) => {
  switch (warning.type) {
    case 'already_shipped':
      return { color: 'bg-red-100 text-red-800', label: `Kiszállítva (${warning.buyer}, ${formatWarningDate(warning.delivery_date)})` };
    case 'in_other_draft':
      return { color: 'bg-yellow-100 text-yellow-800', label: `Másik tervezetben (${warning.buyer}, ${formatWarningDate(warning.delivery_date)})` };
    case 'not_slaughterable': {
      const formattedDate = warning.withdrawal_end_date
        ? new Date(warning.withdrawal_end_date).toLocaleDateString('hu-HU')
        : '?';
      return { color: 'bg-orange-100 text-orange-800', label: `Karencia: ${formattedDate}` };
    }
    case 'not_active':
      return { color: 'bg-gray-100 text-gray-800', label: `Státusz: ${warning.current_status}` };
    default:
      return { color: 'bg-gray-100 text-gray-800', label: 'Figyelmeztetés' };
  }
};

const AnimalSelectorModal: React.FC<AnimalSelectorModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  mode,
  excludeIds = [],
  filterByStatus,
  showStatusFilter = false,
  includeDeliveryWarnings = false,
  title = 'Állat kiválasztása',
  confirmLabel = 'Kiválasztás',
}) => {
  // Reference data for filters and location display
  const [farms, setFarms] = useState<Farm[]>([]);
  const [stables, setStables] = useState<Stable[]>([]);
  const [boxes, setBoxes] = useState<Box[]>([]);

  // Animals & loading
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Cascading filters
  const [farmFilter, setFarmFilter] = useState('');
  const [stableFilter, setStableFilter] = useState('');
  const [boxFilter, setBoxFilter] = useState('');

  // Ear tag search (debounced)
  const [earTagInput, setEarTagInput] = useState('');
  const [earTagFilter, setEarTagFilter] = useState('');

  // User-changeable status filter (initialized from prop)
  const [statusFilter, setStatusFilter] = useState(filterByStatus || '');

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedInfo, setSelectedInfo] = useState<Record<number, SelectedAnimal>>({});
  const [showSummary, setShowSummary] = useState(false);

  // Reset all internal state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Re-initialize status filter from prop when modal opens
      setStatusFilter(filterByStatus || '');
    } else {
      setAnimals([]);
      setPage(1);
      setLastPage(1);
      setTotal(0);
      setFarmFilter('');
      setStableFilter('');
      setBoxFilter('');
      setEarTagInput('');
      setEarTagFilter('');
      setStatusFilter(filterByStatus || '');
      setSelectedIds([]);
      setSelectedInfo({});
      setShowSummary(false);
    }
  }, [isOpen, filterByStatus]);

  // Debounce ear tag input
  useEffect(() => {
    const timer = setTimeout(() => {
      setEarTagFilter(earTagInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [earTagInput]);

  // Load reference data (farms, stables, boxes) when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadReferenceData = async () => {
      try {
        const farmsResponse = await farmApi.getAll();
        setFarms(farmsResponse);

        const allStables: Stable[] = [];
        const allBoxes: Box[] = [];

        for (const farm of farmsResponse) {
          try {
            const [farmStables, farmBoxes] = await Promise.all([
              stableApi.getAll(farm.id),
              boxApi.getAll(farm.id),
            ]);
            allStables.push(...farmStables);
            allBoxes.push(...farmBoxes);
          } catch (error) {
            console.error(`Error loading data for farm ${farm.id}:`, error);
            handleApiError(error, 'Hiba történt a telep adatainak betöltésekor');
          }
        }

        setStables(allStables);
        setBoxes(allBoxes);
      } catch (error) {
        console.error('Error loading reference data:', error);
        handleApiError(error, 'Hiba történt a referencia adatok betöltésekor');
      }
    };

    loadReferenceData();
  }, [isOpen]);

  // Fetch a page of animals from the server
  const loadAnimals = useCallback(async (targetPage: number = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page: targetPage };
      if (farmFilter) params.farm_id = farmFilter;
      if (stableFilter) params.stable_id = stableFilter;
      if (boxFilter) params.box_id = boxFilter;
      if (statusFilter) params.status = statusFilter;
      if (earTagFilter) params.ear_tag = earTagFilter;
      if (includeDeliveryWarnings) params.include_delivery_warnings = 1;

      const response = await animalApi.getAll(params);
      setAnimals(response.data);
      setPage(response.current_page);
      setLastPage(response.last_page);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading animals:', error);
      handleApiError(error, 'Hiba történt az állatok betöltésekor');
    } finally {
      setLoading(false);
    }
  }, [farmFilter, stableFilter, boxFilter, statusFilter, earTagFilter, includeDeliveryWarnings]);

  // Reload animals when modal opens or filters change
  useEffect(() => {
    if (isOpen) {
      setPage(1);
      loadAnimals(1);
    }
  }, [isOpen, loadAnimals]);

  // Client-side exclusion of specific animal IDs
  const excludeIdSet = useMemo(() => new Set(excludeIds), [excludeIds]);

  const visibleAnimals = useMemo(
    () => animals.filter(animal => !excludeIdSet.has(animal.id)),
    [animals, excludeIdSet]
  );

  // Resolve animal location from reference data
  const getAnimalLocation = useCallback((animal: Animal) => {
    if (!animal.current_box_id) return { farm: '-', stable: '-', box: '-' };

    const box = boxes.find(b => b.id === animal.current_box_id);
    if (!box) return { farm: '-', stable: '-', box: '-' };

    const stable = stables.find(s => s.id === box.stable_id);
    if (!stable) return { farm: '-', stable: '-', box: box.name };

    const farm = farms.find(f => f.id === stable.farm_id);

    return {
      farm: farm?.name || '-',
      stable: stable.name || '-',
      box: box.name || '-',
    };
  }, [farms, stables, boxes]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('hu-HU');

  // Filtered reference data for cascading dropdowns
  const filteredStables = useMemo(
    () => farmFilter ? stables.filter(s => s.farm_id === parseInt(farmFilter)) : stables,
    [stables, farmFilter]
  );

  const filteredBoxes = useMemo(
    () => stableFilter ? boxes.filter(b => b.stable_id === parseInt(stableFilter)) : boxes,
    [boxes, stableFilter]
  );

  // --- Selection handlers ---

  const getAnimalWarnings = (animal: Animal): AnimalWarning[] => {
    return (animal as any).delivery_warnings || [];
  };

  const handleRowClick = (animal: Animal) => {
    if (mode === 'single') {
      onSelect([{ id: animal.id, ear_tag: animal.ear_tag, breed: animal.breed, delivery_warnings: getAnimalWarnings(animal) }]);
    }
  };

  const toggleAnimalSelection = (animal: Animal, selected: boolean) => {
    if (selected) {
      setSelectedIds(prev => [...prev, animal.id]);
      setSelectedInfo(prev => ({
        ...prev,
        [animal.id]: { id: animal.id, ear_tag: animal.ear_tag, breed: animal.breed, delivery_warnings: getAnimalWarnings(animal) },
      }));
    } else {
      setSelectedIds(prev => prev.filter(id => id !== animal.id));
      setSelectedInfo(prev => {
        const next = { ...prev };
        delete next[animal.id];
        return next;
      });
    }
  };

  const togglePageSelection = (selected: boolean) => {
    const pageAnimalIds = visibleAnimals.map(a => a.id);
    if (selected) {
      setSelectedIds(prev => Array.from(new Set([...prev, ...pageAnimalIds])));
      setSelectedInfo(prev => {
        const next = { ...prev };
        visibleAnimals.forEach(a => {
          next[a.id] = { id: a.id, ear_tag: a.ear_tag, breed: a.breed, delivery_warnings: getAnimalWarnings(a) };
        });
        return next;
      });
    } else {
      const pageIdSet = new Set(pageAnimalIds);
      setSelectedIds(prev => prev.filter(id => !pageIdSet.has(id)));
      setSelectedInfo(prev => {
        const next = { ...prev };
        pageAnimalIds.forEach(id => { delete next[id]; });
        return next;
      });
    }
  };

  const removeSelected = (id: number) => {
    setSelectedIds(prev => prev.filter(aid => aid !== id));
    setSelectedInfo(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleConfirm = () => {
    const selected = selectedIds.map(id => selectedInfo[id]).filter(Boolean);
    onSelect(selected);
  };

  // --- Filter change handlers (cascade reset) ---
  const handleFarmFilterChange = (value: string) => {
    setFarmFilter(value);
    setStableFilter('');
    setBoxFilter('');
  };

  const handleStableFilterChange = (value: string) => {
    setStableFilter(value);
    setBoxFilter('');
  };

  // --- Derived state ---

  const isAllPageSelected = visibleAnimals.length > 0 &&
    visibleAnimals.every(a => selectedIds.includes(a.id));

  if (!isOpen) return null;

  const hasWarningsCol = includeDeliveryWarnings;
  const colCount = (mode === 'multi' ? 8 : 7) + (hasWarningsCol ? 1 : 0);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>

          {/* Filter Controls */}
          <div className={`mb-4 grid grid-cols-1 ${showStatusFilter ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fülszám keresés
              </label>
              <ClearableInput
                value={earTagInput}
                onChange={setEarTagInput}
                placeholder="Fülszám részlet..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telep szűrő
              </label>
              <ClearableSelect
                value={farmFilter}
                onChange={handleFarmFilterChange}
                placeholder="Minden telep"
              >
                {farms.map((farm) => (
                  <option key={farm.id} value={farm.id}>{farm.name}</option>
                ))}
              </ClearableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Istálló szűrő
              </label>
              <ClearableSelect
                value={stableFilter}
                onChange={handleStableFilterChange}
                placeholder="Minden istálló"
                disabled={!farmFilter}
              >
                {filteredStables.map((stable) => (
                  <option key={stable.id} value={stable.id}>{stable.name}</option>
                ))}
              </ClearableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Box szűrő
              </label>
              <ClearableSelect
                value={boxFilter}
                onChange={setBoxFilter}
                placeholder="Minden box"
                disabled={!stableFilter}
              >
                {filteredBoxes.map((box) => (
                  <option key={box.id} value={box.id}>{box.name}</option>
                ))}
              </ClearableSelect>
            </div>

            {showStatusFilter && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Státusz szűrő
                </label>
                <ClearableSelect
                  value={statusFilter}
                  onChange={setStatusFilter}
                  placeholder="Minden státusz"
                >
                  <option value="aktiv">Aktív</option>
                  <option value="eladott">Eladott</option>
                  <option value="elhullott">Elhullott</option>
                  <option value="levagott">Levágott</option>
                </ClearableSelect>
              </div>
            )}
          </div>

          {/* Animals Table */}
          <div className="relative min-h-[384px] max-h-96 overflow-y-auto">
            {loading && (
              <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-10">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
              </div>
            )}
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-[5]">
                  <tr>
                    {mode === 'multi' && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          onChange={(e) => togglePageSelection(e.target.checked)}
                          checked={isAllPageSelected}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fülszám
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fajta
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Születési dátum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Telep
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Istálló
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Box
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Státusz
                    </th>
                    {hasWarningsCol && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Figyelmeztetések
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {visibleAnimals.length === 0 ? (
                    <tr>
                      <td colSpan={colCount} className="px-6 py-12 text-center text-sm text-gray-500">
                        Nem található állat
                      </td>
                    </tr>
                  ) : (
                    visibleAnimals.map((animal) => {
                      const location = getAnimalLocation(animal);
                      return (
                        <tr
                          key={animal.id}
                          className={`hover:bg-gray-50 ${mode === 'single' ? 'cursor-pointer' : ''}`}
                          onClick={mode === 'single' ? () => handleRowClick(animal) : undefined}
                        >
                          {mode === 'multi' && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(animal.id)}
                                onChange={(e) => toggleAnimalSelection(animal, e.target.checked)}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {animal.ear_tag}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {animal.breed}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {animal.birth_date ? formatDate(animal.birth_date) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {location.farm}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {location.stable}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {location.box}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              animal.status === 'aktiv' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {animal.status}
                            </span>
                          </td>
                          {hasWarningsCol && (() => {
                            const warnings = getAnimalWarnings(animal);
                            return (
                              <td className="px-6 py-4 text-sm text-gray-900">
                                {warnings.length > 0 ? (
                                  <div className="flex flex-col gap-1">
                                    {warnings.map((w, i) => {
                                      const badge = warningBadge(w);
                                      return (
                                        <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                                          {badge.label}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : null}
                              </td>
                            );
                          })()}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
          </div>

          {/* Pagination Controls */}
          {lastPage > 1 && (
            <div className="flex justify-between items-center mt-3 px-1">
              <p className="text-sm text-gray-700">
                Összesen {total} állat
              </p>
              <Pagination page={page} lastPage={lastPage} onPageChange={loadAnimals} disabled={loading} />
            </div>
          )}

          {/* Selected Animals Summary (multi mode) */}
          {mode === 'multi' && selectedIds.length > 0 && (
            <div className="mt-3 border border-green-200 rounded-md bg-green-50">
              <button
                type="button"
                onClick={() => setShowSummary(prev => !prev)}
                className="w-full flex justify-between items-center px-3 py-2 text-sm font-medium text-green-800 hover:bg-green-100 rounded-md"
              >
                <span>{selectedIds.length} állat kiválasztva</span>
                <svg className={`w-4 h-4 transition-transform ${showSummary ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showSummary && (
                <div className="px-3 pb-3 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {selectedIds.map(id => (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white border border-green-300 text-green-800">
                      {selectedInfo[id]?.ear_tag || `#${id}`}
                      <button
                        type="button"
                        onClick={() => removeSelected(id)}
                        className="text-green-500 hover:text-red-500"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end items-center mt-4 pt-4 border-t">
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Mégse
              </button>
              {mode === 'multi' && (
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={selectedIds.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {confirmLabel} ({selectedIds.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimalSelectorModal;
